'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import { parse, tryParse, type ParseError } from '@/lib/swarm/dsl-parser';
import { compile } from '@/lib/swarm/bytecode';
import { type Program, type SimResult, type World } from '@/lib/swarm/types';
import { useAuth } from '@/hooks/useAuth';
import { SwarmCodePanel } from './SwarmCodePanel';
import { SwarmSimPanel, AntInfoOverlay } from './SwarmSimPanel';
import { SwarmBottomBar } from './SwarmBottomBar';

const STORAGE_KEY = 'swarm-code';
const PRACTICE_MAP_COUNT = 12;

const DEFAULT_CODE = `; ── SWARM FORAGER ────────────────────────────
; Random walk to find food, dead reckoning to
; navigate home. Pheromone marks food for others.
; Hit play, then try improving it!

; ── Registers ──
.alias dx   r1           ; x displacement from nest
.alias dy   r2           ; y displacement from nest
.alias ret  r3           ; return address for CALL

; ── Pheromone ──
.const TRAIL CH_RED      ; marks food locations

; ── Roles (visible in simulation viewer) ──
.tag 0 foraging
.tag 1 homing

; ──────────────────────────────────────────────
main:
    CARRYING
    JNE r0 0 go_home
    TAG foraging

; ── SEARCH: find food ─────────────────────────
search:
    SENSE FOOD
    JNE r0 0 grab
    SMELL TRAIL
    JNE r0 0 step
    RANDOM r0 4
    ADD r0 1
step:
    CALL ret move_track
    JMP main

grab:
    CALL ret move_track
    PICKUP
    MARK TRAIL 100
    JMP main

; ── GO HOME: dead reckon toward nest ──────────
go_home:
    TAG homing
    SENSE NEST
    JNE r0 0 deliver
    RANDOM r0 4
    JEQ r0 0 wander       ; 25% random to unstick walls
    CALL ret home_dir
    CALL ret move_track
    JMP main
wander:
    RANDOM r0 4
    ADD r0 1
    CALL ret move_track
    JMP main

deliver:
    CALL ret move_track
    DROP
    JMP main

; ══════════════════════════════════════════════
; SUBROUTINES — CALL ret <label>, return JMP ret
; ══════════════════════════════════════════════

; home_dir: set r0 = direction toward nest
home_dir:
    SET r4 dx
    JGT dx 0 hd_dxp
    SET r4 0
    SUB r4 dx
hd_dxp:
    SET r5 dy
    JGT dy 0 hd_dyp
    SET r5 0
    SUB r5 dy
hd_dyp:
    JGT r5 r4 hd_y
    JGT dx 0 hd_w
    SET r0 2
    JMP ret
hd_w:
    SET r0 4
    JMP ret
hd_y:
    JGT dy 0 hd_n
    SET r0 3
    JMP ret
hd_n:
    SET r0 1
    JMP ret

; move_track: MOVE r0, then update dx/dy
move_track:
    MOVE r0
    JEQ r0 1 mt_n
    JEQ r0 2 mt_e
    JEQ r0 3 mt_s
    SUB dx 1
    JMP ret
mt_n:
    SUB dy 1
    JMP ret
mt_e:
    ADD dx 1
    JMP ret
mt_s:
    ADD dy 1
    JMP ret
`;

export function SwarmWorkspace({ challengeEndDate }: { challengeEndDate?: string }) {
  const competitionClosed = challengeEndDate ? new Date() > new Date(challengeEndDate) : false;
  const { user, loading: authLoading, updateUser } = useAuth();
  const [code, setCode] = useState(DEFAULT_CODE);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        parse(saved);
        setCode(saved);
      } catch {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setHydrated(true);
  }, []);

  const [program, setProgram] = useState<Program | null>(null);
  const [compiledSource, setCompiledSource] = useState<string>('');
  const [sourceLines, setSourceLines] = useState<number[] | undefined>();
  const [errors, setErrors] = useState<ParseError[]>([]);
  const [instrCount, setInstrCount] = useState(0);
  const [tagNames, setTagNames] = useState<Map<number, string>>(new Map());
  const [registerAliases, setRegisterAliases] = useState<Map<number, string>>(new Map());
  const [selectedAntIndex, setSelectedAntIndex] = useState<number | null>(null);
  const [codeTab, setCodeTab] = useState('code');
  const [scores, setScores] = useState<Map<number, SimResult>>(new Map());
  const [scoring, setScoring] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<string | null>(null);
  const [submitMapsCompleted, setSubmitMapsCompleted] = useState<number | null>(null);
  const [submitMapsTotal, setSubmitMapsTotal] = useState<number | null>(null);
  const [scoringSubmission, setScoringSubmission] = useState<{ id: string; code: string; submittedAt: string } | null>(null);
  const cancelRef = useRef(false);
  const worldRef = useRef<World | null>(null);
  const bytecodeRef = useRef<Int32Array>(new Int32Array(0));
  const jumpToLineRef = useRef<((line: number) => void) | null>(null);
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    const result = tryParse(code);
    if (result.ok) {
      const bc = compile(result.program);
      setErrors([]);
      setInstrCount(result.program.instructions.length);
      setSourceLines(result.program.sourceLines);
      setCompiledSource(code);
      setTagNames(result.program.tagNames ?? new Map());
      setRegisterAliases(result.program.registerAliases ?? new Map());

      const prev = bytecodeRef.current;
      if (bc.length !== prev.length || bc.some((v, i) => v !== prev[i])) {
        bytecodeRef.current = bc;
        setProgram(result.program);
      }
    } else {
      setSourceLines(undefined);
      setErrors(result.errors);
      setInstrCount(0);
    }
  }, [code]);

  const handleCodeChange = useCallback((newCode: string) => {
    setCode(newCode);
    localStorage.setItem(STORAGE_KEY, newCode);
  }, []);

  const handleWorldUpdate = useCallback((world: World | null) => {
    worldRef.current = world;
    forceUpdate(n => n + 1);
  }, []);

  const [scoresStale, setScoresStale] = useState(false);

  const handleScoresStale = useCallback(() => {
    setScoresStale(true);
  }, []);

  const handleScoresUpdate = useCallback((newScores: Map<number, SimResult>) => {
    setScores(newScores);
    setScoresStale(false);
  }, []);

  const pollSubmission = useCallback(async (id: string) => {
    setScoring(true);
    cancelRef.current = false;
    setSubmitStatus('Scoring...');
    setSubmitMapsCompleted(null);
    setSubmitMapsTotal(null);

    for (let i = 0; i < 300; i++) {
      if (cancelRef.current) {
        setSubmitStatus('Cancelled');
        setScoring(false);
        localStorage.removeItem('swarm-active-submission');
        setTimeout(() => setSubmitStatus(null), 3000);
        return;
      }
      await new Promise(r => setTimeout(r, 1500));
      try {
        const statusRes = await fetch(`/api/swarm/status?id=${id}`);
        const status = await statusRes.json();
        if (status.status === 'scoring') {
          setSubmitMapsCompleted(status.mapsCompleted);
          setSubmitMapsTotal(status.mapsTotal);
          setSubmitStatus(`Scoring... ${status.mapsCompleted}/${status.mapsTotal} maps`);
        } else if (status.status === 'completed') {
          setSubmitStatus(`Score: ${status.score}/1000 (${status.foodCollected}/${status.foodTotal} food)`);
          setSubmitMapsCompleted(status.mapsTotal);
          setSubmitMapsTotal(status.mapsTotal);
          setScoring(false);
          localStorage.removeItem('swarm-active-submission');
          return;
        } else if (status.status === 'failed' || status.status === 'cancelled') {
          setSubmitStatus(status.status === 'cancelled' ? 'Cancelled' : `Failed: ${status.error}`);
          setScoring(false);
          localStorage.removeItem('swarm-active-submission');
          return;
        }
      } catch {
        // Network error, keep polling
      }
    }
    setSubmitStatus('Timed out. Check back later.');
    setScoring(false);
    localStorage.removeItem('swarm-active-submission');
  }, []);

  // Resume polling if there's an active submission from a previous session
  useEffect(() => {
    const activeId = localStorage.getItem('swarm-active-submission');
    if (activeId) {
      pollSubmission(activeId);
    }
  }, [pollSubmission]);

  const handleCancel = useCallback(async () => {
    cancelRef.current = true;
    const activeId = localStorage.getItem('swarm-active-submission');
    if (activeId) {
      try {
        await fetch('/api/swarm/cancel', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: activeId }),
        });
      } catch { /* polling loop will handle the rest */ }
    }
  }, []);

  const [showCountryModal, setShowCountryModal] = useState(false);

  const doSubmit = useCallback(async (country?: string) => {
    cancelRef.current = true;
    await new Promise(r => setTimeout(r, 100));

    setScoring(true);
    setSubmitStatus('Submitting...');
    setSubmitMapsCompleted(null);
    setSubmitMapsTotal(null);
    try {
      const res = await fetch('/api/swarm/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, ...(country ? { country } : {}) }),
      });
      const data = await res.json();
      if (!data.success) {
        setSubmitStatus(`Error: ${data.error}`);
        setScoring(false);
        return;
      }

      localStorage.setItem('swarm-active-submission', data.id);
      setScoringSubmission({ id: data.id, code, submittedAt: new Date().toISOString() });
      await pollSubmission(data.id);
    } catch {
      setSubmitStatus('Submission failed. Try again.');
      setScoring(false);
    }
  }, [code, pollSubmission]);

  const handleSubmit = useCallback(async () => {
    if (!user) {
      window.location.href = '/api/auth';
      return;
    }
    if (!competitionClosed && !user.country) {
      setShowCountryModal(true);
      return;
    }
    doSubmit();
  }, [user, doSubmit, competitionClosed]);

  const handleCountrySubmit = useCallback((country: string) => {
    setShowCountryModal(false);
    updateUser({ country });
    doSubmit(country);
  }, [doSubmit, updateUser]);

  const selectedAnt = selectedAntIndex !== null ? worldRef.current?.ants[selectedAntIndex] ?? null : null;
  const activeLine = selectedAnt && sourceLines
    ? sourceLines[selectedAnt.pc] ?? null
    : null;

  if (!hydrated) {
    return <div className="h-full bg-surface" />;
  }

  return (
    <div className="h-full flex flex-col bg-surface overflow-hidden">
      <div className="flex-1 flex min-h-0">
        <div className="relative flex-1 min-w-[380px] min-h-0">
          <SwarmCodePanel
            code={code}
            onChange={handleCodeChange}
            errors={errors}
            activeLine={activeLine}
            instrCount={instrCount}
            user={user ?? null}
            onLoadCode={handleCodeChange}
            jumpToLineRef={jumpToLineRef}
            onTabChange={setCodeTab}
            scoringSubmission={scoringSubmission}
            challengeEndDate={challengeEndDate}
          />
          {selectedAntIndex !== null && program && codeTab !== 'leaderboard' && codeTab !== 'admin' && (
            <div className="absolute top-[40px] right-0 bottom-0 w-[140px] z-10 p-1.5 pl-0">
              <AntInfoOverlay
                ants={worldRef.current?.ants ?? null}
                selectedAntIndex={selectedAntIndex}
                program={program}
                tagNames={tagNames}
                registerAliases={registerAliases}
                sourceCode={compiledSource}
                sourceLines={sourceLines}
                onSelectAnt={setSelectedAntIndex}
                onJumpToLine={jumpToLineRef.current ?? undefined}
              />
            </div>
          )}
        </div>
        <div className="flex flex-col min-h-0" style={{ width: 672 }}>
          <SwarmSimPanel
            program={program}
            tagNames={tagNames}
            selectedAntIndex={selectedAntIndex}
            onSelectAnt={setSelectedAntIndex}
            onWorldUpdate={handleWorldUpdate}
            onScoresUpdate={handleScoresUpdate}
            onScoresStale={handleScoresStale}
          />
        </div>
      </div>

      {/* Bottom bar */}
      <SwarmBottomBar
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        scoring={scoring}
        submitStatus={submitStatus}
        submitMapsCompleted={submitMapsCompleted}
        submitMapsTotal={submitMapsTotal}
        scores={scores}
        scoresStale={scoresStale}
        totalMaps={PRACTICE_MAP_COUNT}
        user={user}
        authLoading={authLoading}
        competitionClosed={competitionClosed}
      />

      {showCountryModal && (
        <CountryModal
          onSubmit={handleCountrySubmit}
          onClose={() => setShowCountryModal(false)}
        />
      )}
    </div>
  );
}

const COUNTRIES = [
  'US', 'CA', 'GB', 'DE', 'FR', 'AU', 'IN', 'BR', 'JP', 'KR',
  'MX', 'NL', 'SE', 'CH', 'SG', 'IL', 'PL', 'ES', 'IT', 'AR',
  'CL', 'CO', 'NZ', 'IE', 'PT', 'AT', 'BE', 'DK', 'FI', 'NO',
  'CZ', 'RO', 'HU', 'ZA', 'PH', 'NG', 'KE', 'EG', 'AE', 'SA',
  'TW', 'HK', 'TH', 'VN', 'ID', 'MY', 'PK', 'BD', 'UA', 'TR',
  'RU', 'CN', 'OTHER',
];

const COUNTRY_NAMES: Record<string, string> = {
  US: 'United States', CA: 'Canada', GB: 'United Kingdom', DE: 'Germany',
  FR: 'France', AU: 'Australia', IN: 'India', BR: 'Brazil', JP: 'Japan',
  KR: 'South Korea', MX: 'Mexico', NL: 'Netherlands', SE: 'Sweden',
  CH: 'Switzerland', SG: 'Singapore', IL: 'Israel', PL: 'Poland',
  ES: 'Spain', IT: 'Italy', AR: 'Argentina', CL: 'Chile', CO: 'Colombia',
  NZ: 'New Zealand', IE: 'Ireland', PT: 'Portugal', AT: 'Austria',
  BE: 'Belgium', DK: 'Denmark', FI: 'Finland', NO: 'Norway', CZ: 'Czechia',
  RO: 'Romania', HU: 'Hungary', ZA: 'South Africa', PH: 'Philippines',
  NG: 'Nigeria', KE: 'Kenya', EG: 'Egypt', AE: 'UAE', SA: 'Saudi Arabia',
  TW: 'Taiwan', HK: 'Hong Kong', TH: 'Thailand', VN: 'Vietnam',
  ID: 'Indonesia', MY: 'Malaysia', PK: 'Pakistan', BD: 'Bangladesh',
  UA: 'Ukraine', TR: 'Turkey', RU: 'Russia', CN: 'China', OTHER: 'Other',
};

function CountryModal({ onSubmit, onClose }: { onSubmit: (country: string) => void; onClose: () => void }) {
  const [country, setCountry] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="bg-panel border border-stroke rounded-lg shadow-2xl w-[420px] max-w-[85vw]"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 pt-5 pb-5">
          <h2 className="text-cyan font-bold text-[13px] mb-2">Before you submit</h2>
          <p className="text-secondary text-[11px] leading-relaxed mb-4">
            Select your country of residence. The grand prize is available to US residents only.
            Participants from other countries may still submit and appear on the leaderboard.
          </p>
          <div className="relative">
            <select
              value={country}
              onChange={e => setCountry(e.target.value)}
              className="w-full bg-surface border border-stroke rounded pl-3 pr-9 py-2 text-[11px] text-primary outline-none focus:border-cyan appearance-none cursor-pointer"
            >
              <option value="">Select country...</option>
              {COUNTRIES.map(code => (
                <option key={code} value={code}>{COUNTRY_NAMES[code] ?? code}</option>
              ))}
            </select>
            <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-secondary" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 4.5 L6 7.5 L9 4.5" /></svg>
          </div>
          {country && country !== 'US' && (
            <p className="text-amber text-[10px] mt-2">
              Note: The grand prize is only available to US residents. You can still submit and compete on the leaderboard.
            </p>
          )}
          <p className="text-dim text-[10px] leading-relaxed mt-4 mb-4">
            By submitting, you agree to the{' '}
            <a href="/disclosures" target="_blank" className="text-secondary hover:text-cyan underline underline-offset-2">contest disclosures</a>.
          </p>
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded border border-stroke text-dim hover:text-primary text-[11px] cursor-pointer transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => onSubmit(country)}
              disabled={!country}
              className="px-3 py-1.5 rounded border border-amber/40 hover:border-amber text-secondary hover:text-amber text-[11px] cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
